import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { useDrawingArea, useYScale } from "@mui/x-charts-pro";
import ChartTooltip from "components/Dashboard/ChartTooltip";
import React, { useMemo, useState } from "react";

/**
 * Shared, unit-agnostic threshold rendering for both the Waste Shipments
 * (lb-denominated) and Waste Generated (kg-denominated) facility charts.
 *
 * Each threshold maps to:
 *   - a dashed horizontal `<line>` at its y-position
 *   - a filled dot on the y-axis
 *   - a tooltip row listing every threshold (the hovered one bolded)
 *
 * Thresholds with the same value AND color (e.g. federal LQG and California
 * LQG, both 2,200 lbs) are merged into a single line/dot — two identical
 * dashed lines would just look like one messy double line. The tooltip still
 * lists every threshold individually. Coincident thresholds with *different*
 * colors are staggered by {@link STAGGER_PX} so each color stays readable.
 *
 * **Position math note:** `useYScale` returns absolute SVG y coordinates
 * (mapping the data domain → the same pixel space the y-axis ticks use). We
 * therefore use the scale output directly and do NOT add `top` from
 * `useDrawingArea`. Both consumers (margin.top = 0 in shipments, margin.top >
 * 0 in generated) render correctly with this approach.
 */

/** Minimum vertical gap between rendered threshold lines / dots. */
const STAGGER_PX = 3;

/**
 * Generic axis threshold shape consumed by the shared components. Tiles map
 * their tile-specific threshold types to this shape before passing in.
 */
export type AxisThreshold = {
  /** Stable identifier, used for hover matching in the tooltip. */
  id: string;
  /** Display name shown in the tooltip (e.g. `"Federal LQG"`). */
  name: string;
  /** Numeric value in the chart's y-axis units (lb or kg, whatever the tile picks). */
  value: number;
  /** Line / dot color. */
  color: string;
  /** Pre-formatted value string for the tooltip row (e.g. `"100 kg"` or `"2,200 lbs"`). */
  displayValue: string;
};

type PositionedThreshold = {
  threshold: AxisThreshold;
  /** Ids of every threshold this line/dot represents (merged coincident thresholds included). */
  ids: string[];
  y: number;
};

/**
 * Merge thresholds that would render identically (same value and same color)
 * into one entry, then compute each entry's y position from the kg/lb → pixel
 * scale and walk top-to-bottom, pushing any entry within `STAGGER_PX` of the
 * previous one down so differently-colored lines stay visually distinct.
 */
function usePositionedThresholds(thresholds: AxisThreshold[]): PositionedThreshold[] {
  const yScale = useYScale("y-axis");
  return useMemo(() => {
    if (!yScale) return [];

    const mergedByValueAndColor = new Map<string, { threshold: AxisThreshold; ids: string[] }>();
    for (const threshold of thresholds) {
      const key = `${threshold.value}-${threshold.color}`;
      const existing = mergedByValueAndColor.get(key);
      if (existing) {
        existing.ids.push(threshold.id);
      } else {
        mergedByValueAndColor.set(key, { threshold, ids: [threshold.id] });
      }
    }

    const positioned = [...mergedByValueAndColor.values()]
      .map<{ threshold: AxisThreshold; ids: string[]; naturalY: number } | null>(
        ({ threshold, ids }) => {
          const scaled = yScale(threshold.value);
          return scaled == null ? null : { threshold, ids, naturalY: scaled };
        }
      )
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
      .sort((a, b) => a.naturalY - b.naturalY);

    let previousY: number | null = null;
    return positioned.map(({ threshold, ids, naturalY }) => {
      let y = naturalY;
      if (previousY !== null && y - previousY < STAGGER_PX) {
        y = previousY + STAGGER_PX;
      }
      previousY = y;
      return { threshold, ids, y };
    });
  }, [thresholds, yScale]);
}

/**
 * Dashed horizontal reference lines, one per merged threshold. Same-value,
 * same-color thresholds share a line; coincident lines of different colors are
 * staggered (see {@link STAGGER_PX}) so each color stays readable.
 */
export function ThresholdReferenceLines({ thresholds }: { thresholds: AxisThreshold[] }) {
  const { left, top, width, height } = useDrawingArea();
  const positioned = usePositionedThresholds(thresholds);

  return (
    <g>
      {positioned.map(({ threshold, y }) => {
        if (y < top || y > top + height) return null;
        return (
          <line
            key={`threshold-line-${threshold.id}`}
            x1={left}
            x2={left + width}
            y1={y}
            y2={y}
            stroke={threshold.color}
            strokeDasharray="5,5"
            strokeWidth={2}
            style={{ pointerEvents: "none" }}
          />
        );
      })}
    </g>
  );
}

/**
 * Filled dots on the y-axis, one per merged threshold. Hovering a dot opens a
 * tooltip listing every threshold (with each threshold the dot represents
 * bolded); hovering the y-axis itself shows the same list with no row bolded.
 */
export function ThresholdYAxisIndicators({ thresholds }: { thresholds: AxisThreshold[] }) {
  const { left, top, height } = useDrawingArea();
  const positioned = usePositionedThresholds(thresholds);

  const [showTooltip, setShowTooltip] = useState(false);
  const [hoveredIds, setHoveredIds] = useState<string[]>([]);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const handleAxisEnter = (event: React.MouseEvent) => {
    setShowTooltip(true);
    setHoveredIds([]);
    setMouse({ x: event.clientX, y: event.clientY });
  };
  const handleDotEnter = (ids: string[], event: React.MouseEvent) => {
    setShowTooltip(true);
    setHoveredIds(ids);
    setMouse({ x: event.clientX, y: event.clientY });
  };
  const handleLeave = () => {
    setShowTooltip(false);
    setHoveredIds([]);
  };
  const handleMove = (event: React.MouseEvent) => {
    if (showTooltip) setMouse({ x: event.clientX, y: event.clientY });
  };

  return (
    <>
      <g>
        <rect
          x={left - 60}
          y={top - 20}
          width={60}
          height={height + 40}
          fill="transparent"
          style={{ pointerEvents: "all", cursor: "default" }}
          onMouseEnter={handleAxisEnter}
          onMouseLeave={handleLeave}
          onMouseMove={handleMove}
        />
        {positioned.map(({ threshold, ids, y }) => {
          if (y < top || y > top + height) return null;
          return (
            <g key={`y-axis-threshold-${threshold.id}`}>
              <circle
                cx={left - 10}
                cy={y}
                r={4}
                fill={threshold.color}
                stroke="white"
                strokeWidth={1}
                style={{ pointerEvents: "all" }}
                onMouseEnter={(e) => handleDotEnter(ids, e)}
                onMouseLeave={handleLeave}
                onMouseMove={handleMove}
              />
              <circle
                cx={left - 10}
                cy={y}
                r={12}
                fill="transparent"
                style={{ pointerEvents: "all" }}
                onMouseEnter={(e) => handleDotEnter(ids, e)}
                onMouseLeave={handleLeave}
                onMouseMove={handleMove}
              />
            </g>
          );
        })}
      </g>
      {showTooltip && (
        <Tooltip
          open={true}
          title={<ThresholdAxisTooltip thresholds={thresholds} hoveredIds={hoveredIds} />}
          placement="right"
          slotProps={{
            popper: {
              anchorEl: {
                getBoundingClientRect: () => ({
                  top: mouse.y,
                  left: mouse.x,
                  right: mouse.x,
                  bottom: mouse.y,
                  width: 0,
                  height: 0,
                  x: mouse.x,
                  y: mouse.y,
                  toJSON: () => ({}),
                }),
              },
              style: { pointerEvents: "none" },
            },
            tooltip: {
              sx: { maxWidth: "none", backgroundColor: "transparent", p: 0 },
            },
          }}
        >
          <span style={{ position: "fixed", top: mouse.y, left: mouse.x, pointerEvents: "none" }} />
        </Tooltip>
      )}
    </>
  );
}

function ThresholdAxisTooltip({
  thresholds,
  hoveredIds,
}: {
  thresholds: AxisThreshold[];
  hoveredIds: string[];
}) {
  const sorted = [...thresholds].sort((a, b) => b.value - a.value);
  return (
    <ChartTooltip title="Generator Thresholds">
      <Stack direction="column" gap={1}>
        {sorted.map((t) => {
          const isHovered = hoveredIds.includes(t.id);
          return (
            <Stack direction="row" gap={0.5} key={t.id} alignItems="center">
              <Box
                sx={{
                  width: 20,
                  height: 2,
                  display: "inline-block",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: "2px",
                    background: `repeating-linear-gradient(to right, ${t.color} 0, ${t.color} 3px, transparent 2px, transparent 5px)`,
                  },
                }}
              />
              <Typography variant="body2" fontWeight={isHovered ? 700 : 400}>
                {t.name}
              </Typography>
              <Typography variant="body2" fontWeight={isHovered ? 700 : 400}>
                ({t.displayValue})
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </ChartTooltip>
  );
}
